import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateInvoiceClientMutation,
  useGetInvoiceClientQuery,
  useUpdateInvoiceClientMutation
} from '../../../../../services/endpoints/client/invoiceClient';
import { InvoiceClient } from '../../../../../types/finance.types';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { InvoiceClientForm } from './InvoiceClientForm';

const steps = [{ name: 'Details', form: InvoiceClientForm }];

export interface FormValues {
  id?: number;
  name: string;
  invoiceable_type: string;
  invoiceable_id: number;
  is_personal: boolean;
  created_at: string;
  invoceClient?: InvoiceClient;
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditInvoiceClientProps {
  editing?: boolean;
}

export const AddEditInvoiceClient: React.FC<AddEditInvoiceClientProps> = ({ editing }) => {
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createInvoiceClient] = useCreateInvoiceClientMutation();
  const [updateInvoiceClient] = useUpdateInvoiceClientMutation();
  const { data, error, isLoading } = useGetInvoiceClientQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    invoiceable_type: '',
    invoiceable_id: 0,
    is_personal: false,
    created_at: ''
  });

  const validationSchemas = [
    Yup.object({
      // store_name: Yup.string().required('Store name is required'),
      // total: Yup.number().required('Total is required'),
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading invoiceClient', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.invoiceClient) {
      const { invoiceClient } = data;
      setInitialValues({
        id: invoiceClient.id,
        name: invoiceClient.name,
        invoiceable_type: invoiceClient.invoiceable_type,
        invoiceable_id: invoiceClient.invoiceable_id,
        is_personal: invoiceClient.is_personal,
        created_at: invoiceClient.created_at,
        invoceClient: invoiceClient
      });
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const invoiceClientData = {
        ...formValue
      };
      if (editing) {
        const res = await updateInvoiceClient({
          name: invoiceClientData.name,
          id: invoiceClientData.id
        }).unwrap();
        if (res && res.invoiceClient) {
          addToast(`InvoiceClient updated succesfully`, 'success');
          navigate(`/admin/invoice-clients/${data?.invoiceClient.id}`);
        }
      } else {
        const res = await createInvoiceClient(invoiceClientData).unwrap();
        if (res && res.invoiceClient) {
          navigate(`/admin/invoice-clients/${res.invoiceClient.id}`);
          addToast('InvoiceClient created succesfully', 'success');
        } else throw new Error('A problem happened while creating invoiceClient');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        name: true,
        invoiceable_type: true,
        invoiceable_id: true,
        is_personal: true,
        created_at: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to invoice Client${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing
                ? `/admin/invoice-clients/${data?.invoiceClient.id}`
                : '/admin/invoice-clients'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Invoice Client
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
